import { useEffect, useRef, useState } from 'react';

/**
 * Ritmo del conteo: base fija más un tramo proporcional al salto.
 *
 * La base es generosa porque el salto habitual es de una o dos paradas, y con
 * una duración corta el conteo termina antes de que el ojo lo agarre.
 */
const BASE_MS = 450;
const MS_PER_UNIT = 70;
const MAX_MS = 1400;
/** Cuánto queda a la vista el `+2` / `−3`. */
const DELTA_MS = 1400;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

interface AnimatedNumberProps {
  value: number;
  /** Agrega el cambio (`+2`, `−3`) al lado, con el color de la dirección. */
  showDelta?: boolean;
  /** Para envolver el número, por ejemplo con un `%`. */
  format?: (value: number) => string;
  className?: string;
}

/**
 * Un número que cuenta hasta su nuevo valor en vez de saltar.
 *
 * El reporte se refresca solo cada 30 segundos y reemplaza los datos sin
 * desmontar nada: un valor que pasa de 81 a 83 de golpe es imposible de notar.
 * Contando, el movimiento avisa por sí solo, y como la duración crece con el
 * salto, también comunica cuánto cambió: cerrar diez paradas se ve distinto de
 * cerrar una.
 *
 * La primera aparición NO se anima: pasar de "sin datos" a "con datos" ya es
 * evidente, y contar desde cero en cada carga sería puro ruido.
 */
function AnimatedNumber({
  value,
  showDelta = false,
  format,
  className,
}: AnimatedNumberProps) {
  const [display, setDisplay] = useState(value);
  const [delta, setDelta] = useState<number | null>(null);

  /** Último valor RECIBIDO. Es contra el que se mide el `+2` / `−3`. */
  const previous = useRef(value);
  /** Lo que hay pintado ahora mismo, que durante el conteo no es ninguno de los dos. */
  const painted = useRef(value);
  const frame = useRef<number>();
  const deltaTimeout = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    const from = previous.current;
    previous.current = value;

    if (from === value) return undefined;

    if (prefersReducedMotion()) {
      painted.current = value;
      setDisplay(value);
      return undefined;
    }

    const diff = value - from;
    /**
     * El conteo arranca en lo que se está viendo, no en el objetivo anterior.
     * Un dato que llega con la animación a mitad de camino —el reporte se
     * refresca cada 30 segundos— hacía saltar el número hacia atrás hasta el
     * objetivo viejo antes de empezar a contar de nuevo.
     */
    const origin = painted.current;
    const travel = value - origin;
    const duration = Math.min(MAX_MS, BASE_MS + Math.abs(travel) * MS_PER_UNIT);
    const start = performance.now();

    if (showDelta) {
      setDelta(diff);
      clearTimeout(deltaTimeout.current);
      deltaTimeout.current = setTimeout(() => setDelta(null), DELTA_MS);
    }

    const step = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      // easeOutCubic. Se probó easeOutExpo primero y el conteo no se veía: cubre
      // el 87 % del recorrido en el 30 % del tiempo, así que con un salto de dos
      // o tres unidades el número llega antes de que el ojo lo siga. Éste frena
      // igual al final pero reparte mejor el principio.
      const eased = 1 - (1 - t) ** 3;

      painted.current = Math.round(origin + travel * eased);
      setDisplay(painted.current);
      if (t < 1) frame.current = requestAnimationFrame(step);
    };

    frame.current = requestAnimationFrame(step);

    return () => {
      if (frame.current) cancelAnimationFrame(frame.current);
    };
  }, [value, showDelta]);

  useEffect(
    () => () => {
      if (frame.current) cancelAnimationFrame(frame.current);
      clearTimeout(deltaTimeout.current);
    },
    []
  );

  return (
    <span className={className}>
      {format ? format(display) : display}
      {showDelta && delta !== null && (
        <span
          className={`rr-delta rr-delta--${delta > 0 ? 'up' : 'down'}`}
          aria-hidden="true"
        >
          {delta > 0 ? '+' : '−'}
          {Math.abs(delta)}
        </span>
      )}
    </span>
  );
}

export default AnimatedNumber;
